import { useTranslation } from "react-i18next";
import type { Notification } from "../../../../../store/admin/notificationsStore";
import {
  DENSE_ROW,
  ICON_BTN,
  STATUS_CHIP,
  type ThemeClasses,
} from "../../../shared";
import { formatDate } from "../../../../../utils/getDate";
import { useDisableCRUD } from "../../../../../store/admin/companiesStore";
import { CRUDTooltip } from "../../../../../components/base/CRUDTooltip";
interface NotificationRowProps {
  notification: Notification;
  themeClasses: ThemeClasses;
  onView: () => void;
  onEdit: () => void;
}

const NOTIFICATION_ICONS = [
  {
    id: "warning-red",
    icon: "ri-error-warning-fill",
    color: "text-red-500",
    bgColor: "bg-red-500/10",
  },
  {
    id: "warning-yellow",
    icon: "ri-alert-fill",
    color: "text-yellow-500",
    bgColor: "bg-yellow-500/10",
  },
  {
    id: "chat-blue",
    icon: "ri-chat-3-fill",
    color: "text-blue-500",
    bgColor: "bg-blue-500/10",
  },
  {
    id: "success-green",
    icon: "ri-checkbox-circle-fill",
    color: "text-primary-500",
    bgColor: "bg-primary-500/10",
  },
];

const getIconData = (icon: string) => {
  return (
    NOTIFICATION_ICONS.find((i) => i.id === icon) || {
      icon: "ri-notification-line",
      color: "text-gray-500",
      bgColor: "bg-gray-500/10",
    }
  );
};

const getPriorityColor = (priority: string) => {
  switch (priority) {
    case "high":
      return "bg-red-100 text-red-600";
    case "medium":
      return "bg-yellow-100 text-yellow-600";
    case "low":
      return "bg-primary-100 text-primary-600";
    default:
      return "bg-gray-100 text-gray-600";
  }
};

const getStatusColor = (status: string) => {
  switch (status) {
    case "sent":
      return "bg-primary-100 text-primary-600";
    case "scheduled":
      return "bg-blue-100 text-blue-600";
    case "failed":
      return "bg-red-100 text-red-600";
    default:
      return "bg-gray-100 text-gray-600";
  }
};

export default function NotificationRow({
  notification,
  themeClasses,
  onView,
  onEdit,
}: NotificationRowProps) {
  const { t } = useTranslation("adminSections");
  const iconData = getIconData(notification.icon_id || notification.icon);
  const disableCRUD = useDisableCRUD();
  const neutralBtn = `${ICON_BTN} ${themeClasses.buttonHover} ${themeClasses.buttonText} ${themeClasses.buttonTextHover}`;
  const date = notification.createdAt
    ? formatDate(notification.createdAt._seconds)
    : "-";
  return (
    <div className={`${DENSE_ROW} ${themeClasses.cardBg} ${themeClasses.cardBorder}`}>
      {/* Icono y título */}
      <div
        className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${iconData.bgColor}`}
      >
        <i className={`${iconData.icon} text-base ${iconData.color}`}></i>
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 min-w-0">
          <h3 className={`text-sm font-semibold ${themeClasses.text} truncate`} title={notification.title}>
            {notification.title}
          </h3>
          <span className={`${STATUS_CHIP} ${getStatusColor(notification.status_id)}`}>
            {t(`notifications.status.${notification.status_id}`)}
          </span>
          <span className={`${STATUS_CHIP} hidden sm:inline-flex ${getPriorityColor(notification.priority)}`}>
            {t(`notifications.priority.${notification.priority}`)}
          </span>
        </div>
        <p className={`text-xs ${themeClasses.textSecondary} truncate`}>
          {notification.description}
        </p>
      </div>

      <div className="hidden md:block w-32 shrink-0 min-w-0" title={date}>
        <p className={`text-[11px] ${themeClasses.textSecondary}`}>
          {t("notifications.fields.date")}
        </p>
        <p className={`text-sm ${themeClasses.text} truncate tabular-nums`}>{date}</p>
      </div>

      {/* Acciones */}
      <div className="flex items-center gap-0.5 shrink-0">
        <CRUDTooltip>
          <button
            onClick={onEdit}
            className={neutralBtn}
            aria-label={t("notifications.actions.edit")}
            title={t("notifications.actions.edit")}
            disabled={disableCRUD}
          >
            <i className="ri-edit-line"></i>
          </button>
        </CRUDTooltip>
        <button
          onClick={onView}
          className={neutralBtn}
          aria-label={t("notifications.actions.details")}
          title={t("notifications.actions.details")}
        >
          <i className="ri-eye-line"></i>
        </button>
      </div>
    </div>
  );
}
